import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import DateField from "@/components/DateField";

function Field({ col, value, onChange }) {
  const id = `field-${col.name.replace(/\s+/g, "-")}`;
  if (col.type === "date") return <DateField id={id} value={value} onChange={onChange} />;
  if (col.type === "boolean")
    return <Switch id={id} checked={!!value} onCheckedChange={onChange} data-testid={id} />;
  if (col.type === "binary")
    return <p className="text-xs text-muted-foreground font-mono" data-testid={id}>Binary data (read-only)</p>;
  if (col.type === "text" && (col.atype === 12 || String(value ?? "").length > 80))
    return <Textarea id={id} data-testid={id} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  return (
    <Input id={id} data-testid={id} type={col.type === "number" ? "number" : "text"} step="any"
      value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
  );
}

export default function RecordDialog({ open, record, columns, base, onClose, onSaved }) {
  const isEdit = !!record?.id;
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setValues({ ...(record?.data || {}) }); }, [open, record]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.put(`${base}/records/${record.id}`, { data: values });
      else {
        // New record: leave empty/zero number fields (e.g. totals) blank instead of storing 0.
        const payload = { ...values };
        for (const c of columns) {
          if (c.type === "number") {
            const v = payload[c.name];
            if (v === "" || v === null || v === undefined || Number(v) === 0) payload[c.name] = null;
          }
        }
        await api.post(`${base}/records`, { data: payload });
      }
      toast.success(isEdit ? "Record updated" : "Record added");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="record-modal">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit record" : "Add record"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            {columns.map((c) => (
              <div key={c.name} className="space-y-1.5">
                <Label htmlFor={`field-${c.name.replace(/\s+/g, "-")}`} className="flex items-center gap-2">
                  {c.name} <span className="font-mono text-[9px] uppercase text-muted-foreground">{c.type}</span>
                </Label>
                <Field col={c} value={values[c.name]} onChange={(v) => setValues((s) => ({ ...s, [c.name]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="record-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving} className="hover:bg-blue-600 hover:text-white" data-testid="record-save-button">
              {saving && <Loader2 size={14} className="animate-spin" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
